<script setup lang="ts">
import { computed, useId } from "vue";
import { HPixTextArea } from "@pixelpoint-pattern-library-v2/base/components/headless/base/inputs/HPixTextArea/index";

// The multi-line member of the form-control family: label, required marker, error message and the
// `--pix-field-*` roles exactly as PixTextField, so a message field sits beside text fields without
// a seam. Optional props accept an explicit `undefined` (see HPixTextArea).
type Props = {
  modelValue?: string | undefined;
  label?: string | undefined;
  placeholder?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  error?: string | undefined;
  /** Visible lines; the field grows by dragging its corner, vertically only. */
  rows?: number;
  class?: string | string[] | undefined;
};

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  required: false,
  rows: 4,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [];
}>();

const fieldId = useId();
const errorId = `${fieldId}-error`;
const hasError = computed(() => !!props.error);
</script>

<template>
  <div :class="['pix-text-area__root', props.class]">
    <label v-if="props.label" :for="fieldId" class="pix-text-area__label" :data-required="props.required || undefined">
      {{ props.label }}
    </label>
    <HPixTextArea
      :id="fieldId"
      :model-value="props.modelValue"
      :rows="props.rows"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :required="props.required"
      :aria-required="props.required || undefined"
      :aria-invalid="hasError || undefined"
      :aria-describedby="hasError ? errorId : undefined"
      class="pix-text-area__input"
      :data-error="hasError || undefined"
      @update:model-value="emit('update:modelValue', $event)"
      @blur="emit('blur')"
    />
    <span v-if="hasError" :id="errorId" class="pix-text-area__error" role="alert">
      {{ props.error }}
    </span>
  </div>
</template>

<style>
/* Hooks: none of its own. Reads the form-control family `--pix-field-*` like PixTextField
   (docs/TOKEN_CONTRACT.md, "Form controls"); its height comes from `rows`, not from the family's
   single-row `min-height`. */
@layer pix-components {
  .pix-text-area__root {
    --_pix-text-area-surface: var(--pix-field-surface, var(--color-surface));
    --_pix-text-area-border-color: var(--pix-field-border-color, var(--color-surface-border));
    --_pix-text-area-border-width: var(--pix-field-border-width, var(--border-thin));
    --_pix-text-area-radius: var(--pix-field-radius, 0);
    --_pix-text-area-padding-block: var(--pix-field-padding-block, var(--spacing-small));
    --_pix-text-area-padding-inline: var(--pix-field-padding-inline, var(--spacing-medium));
    --_pix-text-area-shadow: var(--pix-field-shadow, var(--shadow-soft));
    --_pix-text-area-text: var(--pix-field-text, var(--color-text-on-surface));
    --_pix-text-area-placeholder: var(--pix-field-placeholder, var(--color-text-muted));
    --_pix-text-area-font-size: var(--pix-field-font-size, var(--text-base));
    --_pix-text-area-label-size: var(--pix-field-label-size, var(--text-sm));
    --_pix-text-area-label-weight: var(--pix-field-label-weight, 500);
    --_pix-text-area-label-color: var(--pix-field-label-color, var(--color-text));

    display: flex;
    flex-direction: column;
    gap: var(--spacing-xsmall);
  }

  .pix-text-area__label {
    font-size: var(--_pix-text-area-label-size);
    font-weight: var(--_pix-text-area-label-weight);
    color: var(--_pix-text-area-label-color);
  }

  .pix-text-area__label[data-required]::after {
    content: " *" / "";
    color: var(--color-error);
  }

  .pix-text-area__input {
    width: 100%;
    padding: var(--_pix-text-area-padding-block) var(--_pix-text-area-padding-inline);
    border: var(--_pix-text-area-border-width) solid var(--_pix-text-area-border-color);
    border-radius: var(--_pix-text-area-radius);
    background: var(--_pix-text-area-surface);
    box-shadow: var(--_pix-text-area-shadow);
    outline: none;

    /* A textarea does not inherit the page font in every browser; the input's size still wins. */
    font: inherit;
    font-size: var(--_pix-text-area-font-size);
    color: var(--_pix-text-area-text);
    resize: vertical;

    &::placeholder {
      color: var(--_pix-text-area-placeholder);
      opacity: 1; /* Firefox dims placeholders to 54% */
    }

    &:hover:not(:disabled) {
      border-color: var(--color-text-muted);
    }

    &:focus-visible {
      outline: var(--focus-ring-width) solid var(--focus-ring-color);
      outline-offset: var(--focus-ring-offset);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: var(--opacity-disabled-state);
      resize: none;
    }

    &[data-error] {
      --_pix-text-area-border-color: var(--color-error);
    }
  }

  .pix-text-area__error {
    font-size: var(--text-sm);
    color: var(--color-error);
  }
}
</style>
